/* eslint-disable react/jsx-props-no-spreading */
import { useSortable } from '@dnd-kit/sortable';
import { CSS, type Transform } from '@dnd-kit/utilities';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { SxProps, Theme } from '@mui/material';

import CourseInterface from '../../interfaces/courses';
import moodleActive from '../../assets/moodle-active.svg';
import moodleInactive from '../../assets/moodle-inactive.svg';

interface SortableCardProps {
  course: CourseInterface;
  position: number;
}

interface Classes {
  cardStyles: (isDragging: boolean, transform: Transform, transition: string) => SxProps<Theme>;
  badgeStyles: SxProps<Theme>;
  cardContentStyles: SxProps<Theme>;
  cardIconStyles: SxProps<Theme>;
}

const classes: Classes = {
  cardStyles: (isDragging, transform, transition) => ({
    boxShadow: isDragging ? 3 : 1,
    cursor: 'grab',
    height: '140px',
    opacity: isDragging ? 0.5 : 1,
    position: 'relative',
    transform: CSS.Transform.toString(transform),
    transition,
    '&:hover': { boxShadow: 4 },
  }),
  badgeStyles: {
    alignItems: 'center',
    backgroundColor: 'primary.main',
    borderRadius: '50%',
    color: 'white',
    display: 'flex',
    fontSize: '12px',
    fontWeight: 600,
    height: 24,
    justifyContent: 'center',
    lineHeight: 1,
    position: 'absolute',
    right: 8,
    top: 8,
    width: 24,
  },
  cardContentStyles: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    justifyContent: 'space-between',
  },
  cardIconStyles: {
    bottom: 8,
    color: 'primary.main',
    position: 'absolute',
    right: 8,
  },
};

const SortableCard = ({ course, position }: SortableCardProps) => {
  const {
    attributes,
    isDragging,
    listeners,
    transform,
    transition,
    setNodeRef,
  } = useSortable({ id: course.id });
  const title = course.moodle ? 'Conectado a Moodle' : 'No conectado a Moodle';

  return (
    <Grid item xs={12} sm={6} md={4} lg={3}>
      <Card
        {...attributes}
        {...listeners}
        ref={setNodeRef}
        sx={classes.cardStyles(isDragging, transform, transition)}
      >
        <Box sx={classes.badgeStyles}>
          {position + 1}
        </Box>
        <CardContent sx={classes.cardContentStyles}>
          <Typography
            fontSize={16}
            fontWeight={600}
            lineHeight={1.2}
            paddingRight="32px"
            variant="h6"
          >
            {course.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {`ID Interno: ${course.internalCourseId}`}
          </Typography>
        </CardContent>
        <Box sx={classes.cardIconStyles}>
          <Tooltip title={title}>
            <Box
              alt={title}
              component="img"
              src={course.moodle ? moodleActive : moodleInactive}
              sx={{ height: 24, width: 24 }}
            />
          </Tooltip>
        </Box>
      </Card>
    </Grid>
  );
};

export default SortableCard;
